///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { nightLightsMaxLevel, nightLightsTileUrl } from "@/earthbrowser/activities/EarthActivity/tilecache/NightLightsSource"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { ElevationLoader, type TileElevationMetadata } from "@/earthbrowser/utils/loaders/ElevationLoader"
import { logger } from "@/utils/LogDispatcher"
import { animation, easing } from "@stovokor/math/animation"
import { vec2 } from "@stovokor/math/vector"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cTileServerHost = "https://tiles.earthbrowser.com"
const cElevationServerHost = "https://tiles.earthbrowser.com"
const cFadeSpeed = 0.5
// Fade back to the parent before an evicted tile is destroyed; shorter than
// the load fade since evictions cascade a level at a time on zoom-out
const cFadeOutSpeed = 0.35

export class EarthTileMaterial implements Material {
	private tileId: TileId
	public origin: vec2
	public delta: vec2
	public isGlobalTexture: boolean = false
	public tileTexture: Texture
	public elevationTexture: Texture
	public parentElevation: Texture
	// Relief maps (tangent-space normal + sky-view occlusion) baked from the
	// elevation textures once they load (TerrainNormalBaker); the fragment
	// hillshade samples these
	public normalTexture: Texture
	public parentNormal: Texture
	public parentTileTexture: Texture
	public parentElevationDelta = new vec2(0, 0)
	public parentElevationFade: number = 1
	public textureFade: number = 1
	public tileFadeIn: number = 1

	public transparency: boolean = false
	public available: boolean = false

	private textureLoader?: ImageLoader
	private textureLoading: boolean = false
	public textureLoaded: boolean = false

	public hasElevationData: boolean = false
	private elevationLoader?: ElevationLoader
	private elevationLoading: boolean = false
	public elevationReady: boolean = false
	private fadeAnimation: animation
	private fadeStarted: boolean = false
	private fadeOutAnimation: animation
	/** Parent textures share this tile's local coordinates, so fades are possible */
	private hasFadeParent: boolean = false
	/** Blending back toward the parent ahead of eviction (EarthTileSet) */
	public fadingOut: boolean = false
	/** Fade-out finished: the tile shows only parent data and can be destroyed */
	public fadeOutComplete: boolean = false
	public elevationMetadata?: TileElevationMetadata
	/** Called once the tile's own elevation (and its metadata) has loaded */
	public onElevationLoaded?: (metadata: TileElevationMetadata | undefined) => void

	// Night lights (NightLightsSource): the fragment shader samples
	// nightTexture at this tile's local uv transformed by scale/offset. Until
	// the tile's own night tile has loaded (or when the tile is finer than the
	// archive goes) these point at the nearest ancestor's texture with the
	// matching sub-rect, resolved in renderUpdate. Dynamic because they change
	// after the renderer has bound the material
	public nightTexture = new UniformDynamic<Texture>()
	public nightUvScale = new UniformDynamic<number>()
	public nightUvOffset = new UniformDynamic<vec2>()
	/** This tile's own night texture once loaded (blankTexture = no lights here) */
	private nightOwn?: Texture
	private nightLoader?: ImageLoader
	/** Key of the texture currently bound, to skip redundant uniform rebinds */
	private nightBoundKey = ""
	private parentMaterial?: EarthTileMaterial
	private destroyed = false

	private context: StovokorContext

	constructor(context: EarthBrowserContext, tileId: TileId, globalTexture?: Texture, parentMaterial?: EarthTileMaterial) {
		this.context = context
		this.tileId = tileId
		this.origin = tileId.origin
		this.delta = tileId.delta
		this.tileTexture = context.blankTexture
		this.elevationTexture = context.blankElevation
		this.parentElevation = context.blankElevation
		this.normalTexture = context.blankNormal
		this.parentNormal = context.blankNormal
		this.parentTileTexture = context.blankTexture

		// Fade from parent elevation/texture to child
		this.fadeAnimation = new animation()
			.from(1).to(0)
			.duration(cFadeSpeed)
			.easing(easing.cubicInOut)
			.onUpdate((value: number) => {
				this.parentElevationFade = value
				this.textureFade = value
			})
			.onComplete(() => {
				this.parentElevationFade = 0
				this.textureFade = 0
			})

		// Fade back from child to parent (eviction); `from` is set when started
		this.fadeOutAnimation = new animation()
			.to(1)
			.duration(cFadeOutSpeed)
			.easing(easing.cubicInOut)
			.onUpdate((value: number) => {
				this.parentElevationFade = value
				this.textureFade = value
			})
			.onComplete(() => {
				this.parentElevationFade = 1
				this.textureFade = 1
				this.fadeOutComplete = true
			})

		if(globalTexture !== undefined) {
			this.tileTexture = globalTexture
			this.parentTileTexture = globalTexture
			this.isGlobalTexture = true
			this.textureLoaded = true
			this.hasElevationData = false
			this.elevationReady = true
			this.available = true
			this.textureFade = 0
			this.parentElevationFade = 0

		} else if(parentMaterial && !parentMaterial.isGlobalTexture) {
			// Use parent texture for fade - parent has compatible local tile coordinates
			this.hasFadeParent = true
			const childIdx = tileId.childIndex
			const deltaX = 0.5 * (childIdx % 2)
			const deltaY = 0.5 * (Math.floor(childIdx / 2))
			this.parentElevationDelta = new vec2(deltaX, deltaY)
			this.parentElevation = parentMaterial.elevationTexture
			this.parentNormal = parentMaterial.normalTexture
			this.parentTileTexture = parentMaterial.tileTexture

			const metadata = parentMaterial.elevationMetadata
			if(metadata) {
				this.hasElevationData = metadata.children[childIdx]
			} else if(tileId.z === 4) {
				this.hasElevationData = cLevel4Elevations[tileId.y][tileId.x] !== 0
			}

		} else {
			// No valid parent for texture fade (no parent or parent uses global texture)
			// Skip fade entirely to avoid artifacts
			this.textureFade = 0
			this.parentElevationFade = 0
			this.fadeStarted = true  // Prevent fade animation from running

			if(tileId.z < 4) {
				this.hasElevationData = false
			} else {
				const context = this.context as EarthBrowserContext
				this.hasElevationData = context.baseElevation.hasTileAvailable(tileId.x, tileId.y, tileId.z)
			}
		}

		if(!this.isGlobalTexture) {
			this.loadTexture()
		}

		if(this.hasElevationData) {
			// packed elevation bytes: NEAREST filtering is required, linear
			// interpolation across the high/low bytes would corrupt values;
			// the shader decodes texels first and interpolates in meters
			// (shaders/elevation_sample.glsl)
			this.elevationTexture = new Texture(context, 1, 1,
				InternalFormat.RGB8, PixelFormat.RGB, ComponentType.UnsignedByte,
				TextureFilter.Nearest, TextureFilter.Nearest)
			this.normalTexture = context.terrainNormals.createTarget()
			this.loadElevation()
		}

		this.parentMaterial = parentMaterial
		this.nightTexture.value = context.blankTexture
		this.nightUvScale.value = 1
		this.nightUvOffset.value = new vec2(0, 0)
		void this.loadNightLights()
	}

	public destroy(): void {
		this.available = false
		this.destroyed = true

		this.fadeAnimation.stop()
		this.fadeOutAnimation.stop()
		this.textureLoader?.abort()
		this.elevationLoader?.abort()
		this.nightLoader?.abort()
		if(this.nightOwn && this.nightOwn !== this.context.blankTexture) {
			this.nightOwn.destroy()
		}

		// Destroy owned textures even when a load is still in flight - the
		// loaders check aborted before uploading and Texture ignores uploads
		// after destroy, so this can't race; skipping it leaks the GL texture
		if(!this.isGlobalTexture) {
			this.tileTexture.destroy()
		}
		if(this.hasElevationData) {
			this.elevationTexture.destroy()
			this.normalTexture.destroy()
		}
	}

	public get readyToBeAParent(): boolean {
		return (!this.hasElevationData || this.elevationReady) && this.textureLoaded
	}

	/** A texture or elevation fetch is still in flight (failed loads are not pending) */
	public get loadPending(): boolean {
		return this.textureLoading || this.elevationLoading
	}

	private tryStartFadeAnimation(): void {
		// Only start fade animation when both texture and elevation are ready
		if(!this.fadeStarted && this.textureLoaded && (!this.hasElevationData || this.elevationReady)) {
			this.fadeStarted = true
			// Start fade animation to transition from parent texture/elevation to child
			this.fadeAnimation.start()
		}
	}

	/**
	 * Whether eviction can be hidden behind a fade: the tile must be showing
	 * its own imagery over a parent whose textures it can blend back to.
	 */
	public get canFadeOut(): boolean {
		return this.hasFadeParent && this.textureLoaded && this.fadeStarted
	}

	/**
	 * Blend imagery and elevation back to the parent's, so that when the
	 * tile is destroyed and the parent's section reopens nothing changes on
	 * screen. Resumes from wherever a load fade left off.
	 */
	public startFadeOut(): void {
		if(this.fadingOut) {
			return
		}
		this.fadingOut = true
		this.fadeOutComplete = false
		this.fadeAnimation.stop()
		this.fadeOutAnimation.from(this.textureFade).start()
	}

	/** The tile is wanted again: resume the fade toward its own data */
	public cancelFadeOut(): void {
		if(!this.fadingOut) {
			return
		}
		this.fadingOut = false
		this.fadeOutComplete = false
		this.fadeOutAnimation.stop()
		this.fadeAnimation.from(this.textureFade).start()
	}

	/**
	 * Fetch this tile's night-lights tile, if the archive goes this deep. An
	 * empty (204) or failed response means no lights here; finer tiles than
	 * the archive keep sampling their ancestor (see renderUpdate).
	 */
	private async loadNightLights(): Promise<void> {
		const maxLevel = await nightLightsMaxLevel()
		if(this.destroyed || this.tileId.z > maxLevel) {
			return
		}
		const texture = new Texture(this.context)
		this.nightLoader = new ImageLoader(nightLightsTileUrl(this.tileId))
		try {
			const success = await this.nightLoader.loadIntoTexture(texture)
			if(this.destroyed) {
				texture.destroy()
				return
			}
			if(success) {
				this.nightOwn = texture
			} else {
				texture.destroy()
				this.nightOwn = this.context.blankTexture
			}
		} catch {
			texture.destroy()
			if(!this.destroyed) {
				this.nightOwn = this.context.blankTexture
			}
		}
		this.nightLoader = undefined
	}

	/**
	 * Point the night-lights uniforms at this tile's own texture once it has
	 * loaded, otherwise at the nearest ancestor that has one, with the uv
	 * sub-rect composed down the chain (each step maps a child's uv into its
	 * parent's quadrant). Per frame since ancestors load independently.
	 */
	public renderUpdate(): void {
		const resolved = EarthTileMaterial.resolveNightLights(this)
		if(resolved.key !== this.nightBoundKey) {
			this.nightBoundKey = resolved.key
			this.nightTexture.value = resolved.texture ?? this.context.blankTexture
			this.nightUvScale.value = resolved.scale
			this.nightUvOffset.value = resolved.offset
		}
	}

	/** Key of the tile whose night texture is bound ("" while none has loaded) */
	public get nightSource(): string {
		return this.nightBoundKey
	}

	/** Walk up from `start` to the nearest material with a loaded night tile */
	private static resolveNightLights(start: EarthTileMaterial): { key: string, texture?: Texture, scale: number, offset: vec2 } {
		let scale = 1
		let offsetX = 0
		let offsetY = 0
		let material: EarthTileMaterial | undefined = start
		while(material && (!material.nightOwn || material.destroyed)) {
			const childIdx = material.tileId.childIndex
			scale *= 0.5
			offsetX = offsetX * 0.5 + 0.5 * (childIdx % 2)
			offsetY = offsetY * 0.5 + 0.5 * Math.floor(childIdx / 2)
			material = material.parentMaterial
		}
		return { key: material?.tileId.key ?? "", texture: material?.nightOwn, scale, offset: new vec2(offsetX, offsetY) }
	}

	private loadTexture(): void {
		if(this.textureLoaded || this.textureLoading) {
			return
		}
		this.textureLoading = true

		// inverse indices are used in the e0cloudless geopackage; the
		// tile-server /visual proxy takes plain z/y/x and zero-pads the
		// B2 key itself
		const invIndex = this.tileId.invIndex
		const url = `${cTileServerHost}/visual/${invIndex.z}/${invIndex.y}/${invIndex.x}.jpg`
		this.tileTexture = new Texture(this.context)
		this.textureLoader = new ImageLoader(url)
		this.textureLoader.loadIntoTexture(this.tileTexture)
			.then((success) => {
				this.available = success
				this.textureLoaded = success
				this.textureLoader = undefined
				this.textureLoading = false
				if(success) {
					this.tryStartFadeAnimation()
				}
			})
			.catch(error => {
				if(this.textureLoader?.aborted) {
					logger.log(`Aborted loading tile ${this.tileId.key}`)
				} else {
					if(error instanceof Error) {
						logger.log(`Failed to load tile ${this.tileId.key}: ${error.message}`)
					} else {
						logger.log(`Failed to load tile ${this.tileId.key}: ${error}`)
					}
				}
				this.textureLoader = undefined
				this.textureLoading = false
				this.available = false
			})
	}

	private loadElevation(): void {
		if(this.elevationReady || this.elevationLoading) {
			return
		}
		this.elevationLoading = true

		const tileId = this.tileId
		const elevationURL = `${cElevationServerHost}/elevation/${tileId.z}/${tileId.y}/${tileId.x}.webp`
		this.elevationLoader = new ElevationLoader(elevationURL)
		this.elevationLoader.loadIntoTexture(this.elevationTexture)
			.then((success: boolean) => {
				if(success) {
					this.elevationMetadata = this.elevationLoader?.elevationMetadata
					// Bake the relief map before the tile reports ready so
					// the first frame already samples it
					const context = this.context as EarthBrowserContext
					context.terrainNormals.bake(this.elevationTexture, this.origin, this.delta, context.terrainExaggeration, this.normalTexture)
					this.elevationReady = true
					this.onElevationLoaded?.(this.elevationMetadata)
					this.tryStartFadeAnimation()
				} else {
					// warn (not error) so failed tile loads don't become Sentry events
					logger.warn(`Failed to load elevation ${tileId.key}`)
					this.hasElevationData = false
					this.elevationTexture.destroy()
					this.normalTexture.destroy()
				}
				this.elevationLoader = undefined
				this.elevationLoading = false
			})
			.catch(_error => {
				if(!(this.elevationLoader?.aborted)) {
					logger.warn(`Failed to load elevation ${tileId.key}`)
				}
				this.elevationLoader = undefined
				this.elevationLoading = false
			})
	}
}

const cLevel4Elevations: number[][] = [
[0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
[0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
[1,1,1,1,1,1,1,1,1,1,1,1,1,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
[1,1,1,1,1,1,1,1,1,1,1,1,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
[0,0,0,0,1,1,1,1,1,1,1,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0],
[1,1,0,0,0,1,1,1,1,1,1,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0],
[1,1,1,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
[1,1,1,0,0,0,1,1,1,1,1,1,0,0,1,1,1,1,1,1,1,0,1,1,1,1,1,1,1,1,1,1],
[1,1,1,1,0,0,0,1,1,1,1,1,1,1,1,0,1,1,1,1,1,1,1,0,1,1,1,1,1,1,1,1],
[1,1,1,1,1,0,0,0,0,1,1,1,1,1,0,1,1,1,1,1,1,1,0,0,1,0,1,1,1,1,1,1],
[1,0,1,1,1,0,1,0,1,1,1,1,1,0,0,0,0,1,1,1,1,0,0,0,0,1,1,1,1,1,1,1],
[1,0,0,0,0,0,0,0,1,1,1,1,0,0,1,1,0,1,1,0,1,0,1,0,0,0,1,1,1,1,1,1],
[1,0,0,0,0,0,0,0,0,1,1,0,1,0,0,0,1,0,0,1,1,0,1,0,0,0,0,0,0,0,1,1],
[0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
[0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
[0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]]